'use client';

import { useRouter } from 'next/navigation';
import { ChangeEvent, FormEvent, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { isToday } from '@alltrails/data-formatting/dateTimeConverters';
import Button from '@alltrails/denali/components/Button';
import DatePicker from '@alltrails/denali/components/DatePicker';
import TextArea from '@alltrails/denali/components/TextArea';
import TextInput from '@alltrails/denali/components/TextInput';
import { GiftCustomizeParams, UPDATE_GIFT_CUSTOMIZE_FORM, UPDATE_SERVER_ERRORS, useGiftFlowContext } from '../../../utils/giftContext';
import { hasFormErrors, validateGiftForm } from '../../../utils/GiftFlowHelper';
import GiftCard from '../GiftCard/GiftCard';
import GiftCardRadioButtons from '../GiftCardRadioButtons/GiftCardRadioButtons';
import GiftTray from '../GiftTray/GiftTray';
import ToggleRadioButtons from '../ToggleRadioButtons/ToggleRadioButtons';
import styles from './CustomizeGiftForm.module.scss';

const LABELS = defineMessages({
  EMAIL_ADDRESS: { defaultMessage: 'Email address' },
  MESSAGE: { defaultMessage: 'Message (500 characters max)' },
  TO: { defaultMessage: 'To:' },
  FROM: { defaultMessage: 'From:' },
  NAME: { defaultMessage: 'Name' }
});

const CustomizeGiftForm = () => {
  const router = useRouter();
  const user = useUser();
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const {
    giftFlowState: { giftCustomizeParams, serverErrors },
    dispatchGiftFlow
  } = useGiftFlowContext();

  const {
    formattedDefaultMessages: { EMAIL_ADDRESS, MESSAGE, TO, FROM, NAME }
  } = useFormatMessage(LABELS);

  const [formParams, updateFormParams] = useState<GiftCustomizeParams>({ ...giftCustomizeParams });
  const [hasSubmitted, updateHasSubmitted] = useState(false);
  const [renderBottomTray, toggleRenderBottomTray] = useState(false);

  const printEmailOptions = [
    { label: intl.formatMessage({ defaultMessage: 'Email' }), value: 'email', name: 'sendMethod' },
    { label: intl.formatMessage({ defaultMessage: 'Print' }), value: 'print', name: 'sendMethod' }
  ];

  const updateForm = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    updateFormParams((prevState: GiftCustomizeParams) => ({ ...prevState, [name]: value }));
  };

  const navigateTo = (path: string) => {
    router.push(path);
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  };

  const handleDateChange = (sendDate: Date) => {
    updateFormParams((prevState: GiftCustomizeParams) => ({ ...prevState, sendDate }));
  };

  const checkErrorMessage = (name: string, value: string, validate: boolean) => {
    if (!validate) {
      return '';
    }

    return validateGiftForm(name, value);
  };

  const checkServerError = (value: string) => {
    if (serverErrors.length === 0) {
      return '';
    }
    const error = serverErrors.find((err: any) => err.target === value);
    return error ? error.message : '';
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!hasSubmitted) {
      updateHasSubmitted(true);
    }

    if (hasFormErrors(formParams)) {
      return;
    }

    if (serverErrors.length > 0) {
      dispatchGiftFlow({ type: UPDATE_SERVER_ERRORS, payload: [] });
    }

    dispatchGiftFlow({
      type: UPDATE_GIFT_CUSTOMIZE_FORM,
      payload: formParams
    });

    if (user) {
      navigateTo('/gift/payment');
    } else {
      toggleRenderBottomTray(true);
    }
  };

  const messageFieldError = () => {
    if (formParams.message.trim().length > 500) {
      return <FormattedMessage defaultMessage="500 character maximum" />;
    }

    return '';
  };

  return (
    <div className={styles.container}>
      <div className={styles.cardColumn}>
        <div className={styles.fixedContainer}>
          <GiftCard variant={formParams.variant} />
        </div>
      </div>
      <form className={styles.formColumn} onSubmit={handleFormSubmit}>
        <h2 className={styles.formHeader}>
          <FormattedMessage defaultMessage="Customize your gift" />
        </h2>
        <div className={styles.formSectionSm}>
          <div className={styles.mobileGiftCardContainer}>
            <GiftCard variant={formParams.variant} />
          </div>
          <GiftCardRadioButtons onChange={updateForm} selectedVariant={formParams.variant} />
        </div>
        <div className={styles.formSectionLg}>
          <div className={styles.label}>
            <FormattedMessage defaultMessage="How do you want to send it?" />
          </div>
          <ToggleRadioButtons options={printEmailOptions} handleOnChange={updateForm} activeOption={formParams.sendMethod} />
        </div>
        <div className={styles.formSection}>
          <div className={styles.inputContainer}>
            <TextInput
              name="recipientName"
              errorText={checkErrorMessage('recipientName', formParams.recipientName, hasSubmitted)}
              value={formParams.recipientName}
              labelText={<span className={styles.label}>{TO}</span>}
              placeholder={NAME}
              onChange={value => {
                updateFormParams((prevState: GiftCustomizeParams) => ({ ...prevState, recipientName: value }));
              }}
            />
          </div>
          {formParams.sendMethod === printEmailOptions[0].value && (
            <div className={styles.inputContainer}>
              <TextInput
                name="recipientEmail"
                errorText={
                  checkErrorMessage('recipientEmail', formParams.recipientEmail, hasSubmitted) || checkServerError(formParams.recipientEmail)
                }
                value={formParams.recipientEmail}
                labelText={EMAIL_ADDRESS}
                placeholder={EMAIL_ADDRESS}
                onChange={value => {
                  updateFormParams((prevState: GiftCustomizeParams) => ({ ...prevState, recipientEmail: value }));
                }}
              />
            </div>
          )}
          <div className={styles.inputContainer}>
            <TextArea
              name="message"
              errorText={messageFieldError()}
              value={formParams.message}
              labelText={MESSAGE}
              placeholder={MESSAGE}
              onChange={value => {
                updateFormParams((prevState: GiftCustomizeParams) => ({ ...prevState, message: value }));
              }}
              rows={9}
            />
          </div>
        </div>
        <div className={styles.formSection}>
          <div className={styles.inputContainer}>
            <TextInput
              name="senderName"
              errorText={checkErrorMessage('senderName', formParams.senderName, hasSubmitted)}
              value={formParams.senderName}
              labelText={<span className={styles.label}>{FROM}</span>}
              placeholder={NAME}
              onChange={value => {
                updateFormParams((prevState: GiftCustomizeParams) => ({ ...prevState, senderName: value }));
              }}
            />
          </div>
          <div className={styles.inputContainer}>
            <TextInput
              name="senderEmail"
              errorText={checkErrorMessage('senderEmail', formParams.senderEmail, hasSubmitted) || checkServerError(formParams.senderEmail)}
              value={formParams.senderEmail}
              labelText={EMAIL_ADDRESS}
              placeholder={EMAIL_ADDRESS}
              onChange={value => {
                updateFormParams((prevState: GiftCustomizeParams) => ({ ...prevState, senderEmail: value }));
              }}
            />
          </div>
        </div>
        {formParams.sendMethod === printEmailOptions[0].value && (
          <div className={styles.formSection}>
            <div className={styles.label}>
              <FormattedMessage defaultMessage="Send date:" />{' '}
              <span className={styles.sendDateHelperText}>
                {isToday(formParams.sendDate) ? (
                  <FormattedMessage defaultMessage="Send today." />
                ) : (
                  <FormattedMessage defaultMessage="Send at a later date." />
                )}
              </span>
            </div>
            <DatePicker
              selected={formParams.sendDate}
              onSelect={(date?: Date) => {
                if (!date) {
                  return;
                }
                handleDateChange(date);
              }}
              mode="single"
              validDates={{ from: new Date() }}
              languageRegionCode={languageRegionCode}
              hideClearDate
            />
          </div>
        )}
        <div className={styles.formSection}>
          <Button text={<FormattedMessage defaultMessage="Next" />} type="submit" variant="primary" width="full" />
        </div>
      </form>
      {renderBottomTray && <GiftTray closeTray={() => toggleRenderBottomTray(false)} />}
    </div>
  );
};

export default CustomizeGiftForm;
